import {
  Button,
  Div,
  H3,
  Icon,
  Img,
  P,
  PlusIcon,
  SuccessToast,
  deleteMenuTypeItemById,
  noModifierGroups,
  reportError,
  updateMenuModifierOptionsOrder,
} from '@vgrubs/components';
import {
  ItemType,
  MenuV2Response,
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import { isAdmin } from 'components/src/helpers/appType';
import { Dispatch, SetStateAction, useState } from 'react';
import { useParams } from 'react-router';
import {
  groupsWithNewOptionsOrder,
  toOptionsOrder,
} from '../../helpers/modifierOptionsOrder';
import {
  MenuTaxRatesModal,
  MenuTaxRatesTarget,
} from '../../taxRates/MenuTaxRatesModal';
import { AddNewModifierGroupModal } from './AddNewModifierGroupModal';
import { DeleteModifierModal } from './DeleteModifierModal';
import { ModifierGroupComponent } from './ModifierComponents';
import { EditOptionPriceModal } from './EditOptionPriceModal';
import { EditModifierModal } from './EditModifierModal';

const removeModifierGroupById = (data: MenuV2Response, idToRemove: number) => {
  return {
    ...data,
    modifierGroups: data.modifierGroups.filter((c) => c.id !== idToRemove),
  };
};

export const Modifiers = ({
  menu,
  setMenu,
  setLoading,
  handleChangeShouldPublish,
  isPos,
  allowedToEditMenuPrice,
}: {
  menu: MenuV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  setLoading: Dispatch<SetStateAction<boolean>>;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
  isPos: boolean;
  allowedToEditMenuPrice: boolean;
}) => {
  const [addNewModal, setAddNewModal] = useState(false);
  const [editOptionPriceModal, setEditOptionPriceModal] =
    useState<ModifierItemV2Response | null>(null);
  const [editModifierModal, setEditModifierModal] =
    useState<ModifierItemV2Response | null>(null);
  const [editModal, setEditModal] =
    useState<ModifierGroupItemV2Response | null>(null);
  const [duplicateModal, setDuplicateModal] =
    useState<ModifierGroupItemV2Response | null>(null);
  const [deleteModal, setDeleteModal] = useState<number | null>(null);
  const [taxRatesModal, setTaxRatesModal] = useState<MenuTaxRatesTarget | null>(
    null
  );
  /** The menu as it was when sorting started, for Cancel Sorting. */
  const [isSorting, setIsSorting] = useState<MenuV2Response | null>(null);

  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
  };

  const isBrand = brandId !== '0';

  // Admin only, and never on a POS location - the register owns the taxes
  // there and every write answers 400.
  const canEditTaxRates = isAdmin && !isPos;

  async function handleDeleteItem() {
    if (!deleteModal) return;
    try {
      setLoading(true);
      const { data } = await deleteMenuTypeItemById(
        locationId,
        brandId,
        deleteModal,
        ItemType.ModifierGroup
      );

      setMenu((prev) =>
        !prev ? prev : removeModifierGroupById(prev, deleteModal)
      );
      handleChangeShouldPublish(true);
      SuccessToast(data.message);
      setDeleteModal(null);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleReorderOptions = (
    groupId: number,
    options: ModifierItemV2Response[]
  ) =>
    setMenu((prev) =>
      !prev
        ? prev
        : {
            ...prev,
            modifierGroups: prev.modifierGroups.map((g) =>
              g.id === groupId ? { ...g, options } : g
            ),
          }
    );

  /**
   * One request per group whose order changed. On a failure the list shows
   * what the server holds: the last answer that came back, or the order from
   * before sorting when none did.
   */
  async function handleSaveOrder() {
    if (!isSorting || !menu) return;
    const changed = groupsWithNewOptionsOrder(menu, isSorting);
    if (changed.length === 0) return setIsSorting(null);

    let saved: MenuV2Response | null = null;
    try {
      setLoading(true);
      let message = '';
      for (const group of changed) {
        const { data } = await updateMenuModifierOptionsOrder(
          locationId,
          brandId,
          group.id,
          toOptionsOrder(group.options.map((o) => o.id))
        );
        saved = data.data;
        message = data.message;
      }
      SuccessToast(message);
    } catch (error) {
      reportError(error);
    } finally {
      setMenu(saved ?? isSorting);
      if (saved) handleChangeShouldPublish(true);
      setIsSorting(null);
      setLoading(false);
    }
  }

  const SortingButtons = () => (
    <>
      {isSorting && (
        <Button
          $selected
          $small
          $gray
          $w="150px"
          $wSM="calc(50% - 8px)"
          onClick={() => {
            setMenu(isSorting);
            setIsSorting(null);
          }}
        >
          Cancel Sorting
        </Button>
      )}
      <Button
        $selected={!!isSorting}
        $small
        $w="150px"
        $wSM={!isSorting ? '100%' : 'calc(50% - 8px)'}
        onClick={() => (isSorting ? handleSaveOrder() : setIsSorting(menu))}
      >
        {isSorting ? 'Save Sorting' : 'Sort Modifiers'}
      </Button>
    </>
  );

  const canSort = !isPos && !!menu?.modifierGroups?.length;

  const AddModifierGroupButton = () => {
    return (
      <Button $selected onClick={() => setAddNewModal(true)}>
        Add Modifier Group
      </Button>
    );
  };

  return (
    <Div>
      <Div $flex $between $alignCenter $mb="20">
        <H3 $mb="0">
          {menu?.modifierGroups && menu?.modifierGroups?.length < 1
            ? 'Modifier Group'
            : 'Modifier Groups'}{' '}
          ({menu?.modifierGroups?.length})
        </H3>

        <Div $flex $alignCenter $gap="16px">
          {canSort && (
            <Div $flex $gap="16px" $desktop>
              <SortingButtons />
            </Div>
          )}
          {!isPos &&
            menu?.modifierGroups &&
            menu?.modifierGroups?.length > 0 && (
              <Icon $pointer onClick={() => setAddNewModal(true)}>
                <PlusIcon />
              </Icon>
            )}
        </Div>
      </Div>

      {canSort && (
        <Div $flex $gap="16px" $mobile $mb="20">
          <SortingButtons />
        </Div>
      )}

      {menu?.modifierGroups?.map((item) => (
        <ModifierGroupComponent
          key={item.id}
          item={item}
          isSorting={!!isSorting}
          onReorderOptions={(options) => handleReorderOptions(item.id, options)}
          onClickDelete={(id) => setDeleteModal(id)}
          onClick={(item) => setEditModal(item)}
          onClickDuplicate={(item) => setDuplicateModal(item)}
          isPos={isPos}
          handleEditOptionPrice={(item) => setEditOptionPriceModal(item)}
          handleEditModifier={(item) => setEditModifierModal(item)}
          handleEditTaxRates={
            canEditTaxRates
              ? (option) =>
                  setTaxRatesModal({
                    kind: 'option',
                    id: option.id,
                    name: option.name,
                    taxRates: option.taxRates ?? [],
                    taxRatesOverridden: !!option.taxRatesOverridden,
                  })
              : undefined
          }
          isBrand={isBrand}
          allowedToEditMenuPrice={allowedToEditMenuPrice}
        />
      ))}

      {menu?.modifierGroups && menu?.modifierGroups?.length < 1 && (
        <Div $flex $column $alignCenter>
          <Img alt="" $width="180px" src={noModifierGroups} $mb="24" />
          <H3 $mb="24" $center>
            No Modifier Groups
          </H3>
          <P $mb="32" $center>
            This provider currently don’t have <br /> menu modifier groups.
          </P>
          {!isPos && <AddModifierGroupButton />}
        </Div>
      )}

      <AddNewModifierGroupModal
        addNewModal={addNewModal}
        closeModal={() => {
          setAddNewModal(false);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <AddNewModifierGroupModal
        addNewModal={editModal}
        closeModal={() => {
          setEditModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        edit
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <AddNewModifierGroupModal
        addNewModal={duplicateModal}
        closeModal={() => {
          setDuplicateModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        duplicate
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <DeleteModifierModal
        deleteModal={!!deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        handleDeleteItem={handleDeleteItem}
      />

      <EditOptionPriceModal
        editOptionPriceModal={editOptionPriceModal}
        closeModal={() => {
          setEditOptionPriceModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <EditModifierModal
        editModifierModal={editModifierModal}
        closeModal={() => {
          setEditModifierModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />

      <MenuTaxRatesModal
        target={taxRatesModal}
        closeModal={() => {
          setTaxRatesModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        handleChangeShouldPublish={handleChangeShouldPublish}
      />
    </Div>
  );
};
